<template>
  <div class="rn-wrapper">
    <svg ref="svg" class="rn-svg" :viewBox="viewBox" preserveAspectRatio="xMidYMid meet">
      <g ref="content">
        <path
          v-if="hasIntralaboral"
          :d="edgePath(hubCx, hubCy, hubRadius, intralaboralPos.cx, intralaboralPos.cy, intraRadius)"
          class="rn-link"
          :style="{ stroke: DOMAIN_COLORS.intra }"
        />
        <path
          v-if="hasExtra"
          :d="edgePath(hubCx, hubCy, hubRadius, extraPos.cx, extraPos.cy, extraRadius)"
          class="rn-link"
          :style="{ stroke: DOMAIN_COLORS.extra }"
        />
        <path
          v-if="hasIndiv"
          :d="edgePath(hubCx, hubCy, hubRadius, indivPos.cx, indivPos.cy, indivRadius)"
          class="rn-link"
          :style="{ stroke: DOMAIN_COLORS.indiv }"
        />

        <circle
          :cx="hubCx"
          :cy="hubCy"
          :r="hubRadius"
          :class="{ 'rn-hub-circle': true, 'rn-hub-circle--nomodel': isMainNoModel }"
          v-tooltip.top="hubTooltipConfig"
          v-on:click="onHubClick"
        />
        <text
          :x="hubCx"
          :y="hubCy + 4"
          :class="{ 'rn-hub-label': true, 'rn-hub-label--nomodel': isMainNoModel }"
          text-anchor="middle"
        >
          {{ effectLabel }}
        </text>

        <intralaboral-node
          v-if="hasIntralaboral"
          :analysis="mainAnalysis"
          label="Intralaboral"
          :effect-label="effectLabel"
          :cx="intralaboralPos.cx"
          :cy="intralaboralPos.cy"
          :hub-cx="hubCx"
          :hub-cy="hubCy"
          :angle-deg="ANGLES.intralaboral"
          :expanded="expandedIntralaboral"
          :color="DOMAIN_COLORS.intra"
          :wash="DOMAIN_WASH.intra"
          v-on:toggle="onToggleIntralaboral"
        />

        <domain-node
          v-if="hasExtra"
          :analysis="extraAnalysis"
          label="Extralaboral"
          :effect-label="effectLabel"
          :cx="extraPos.cx"
          :cy="extraPos.cy"
          :color="DOMAIN_COLORS.extra"
          :wash="DOMAIN_WASH.extra"
        />

        <domain-node
          v-if="hasIndiv"
          :analysis="indivAnalysis"
          label="Individual"
          :effect-label="effectLabel"
          :cx="indivPos.cx"
          :cy="indivPos.cy"
          :color="DOMAIN_COLORS.indiv"
          :wash="DOMAIN_WASH.indiv"
        />
      </g>
    </svg>

    <!-- design.md corrección 2026-09-15: "Cómo se calculó esto" no vive
         dentro de las burbujas (confirmado contra el Artifact) — es un
         disclosure independiente por dominio, debajo del lienzo.
         Corrección 2026-09-22 (pedido de Esteban): solo visible para admin. -->
    <div v-if="isAdmin" class="rn-disclosures">
      <div v-if="hasIntralaboral && !mainAnalysis.no_model" class="rn-disclosure-item">
        <p class="rn-disclosure-item__label">Intralaboral</p>
        <analysis-disclosure :analysis="mainAnalysis" />
      </div>
      <div v-if="hasExtra && !extraAnalysis.no_model" class="rn-disclosure-item">
        <p class="rn-disclosure-item__label">Extralaboral</p>
        <analysis-disclosure :analysis="extraAnalysis" />
      </div>
      <div v-if="hasIndiv && !indivAnalysis.no_model" class="rn-disclosure-item">
        <p class="rn-disclosure-item__label">Individual</p>
        <analysis-disclosure :analysis="indivAnalysis" />
      </div>
    </div>

    <!-- Pedido de Esteban 2026-09-30: misma explicación de línea punteada que
         ya tiene el Sankey, para que el significado del borde sea consistente
         entre las dos vistas. -->
    <details class="rn-legend" open>
      <summary>Leyenda de bordes</summary>
      <div class="rn-legend-body">
        Borde sólido = relación positiva, borde punteado = negativa.
      </div>
    </details>
  </div>
</template>

<script>
import {
  roundRSquaredAdj,
  nodeSize,
  nonConstantCoefficients,
  getConditionLabel,
  NOMODEL_RADIUS,
} from "./regression-helpers";
import { mapState } from "vuex";
import IntralaboralNode from "./IntralaboralNode.vue";
import DomainNode from "./DomainNode.vue";
import AnalysisDisclosure from "./AnalysisDisclosure.vue";

const ANGLES = {
  intralaboral: 0,
  individual: 285,
  extralaboral: 75,
};

// design.md §9 — colores por dominio del Artifact "Regresión FRP" (2026-09-14),
// no una paleta nueva: intra/extra/indiv tal como quedaron en el mockup aprobado.
const DOMAIN_COLORS = { intra: "#6b2fae", extra: "#0e9fa1", indiv: "#c98a1f" };
const DOMAIN_WASH = { intra: "#f2eafa", extra: "#e6f6f6", indiv: "#faf1e0" };

const VB_MIN_W = 460;
const VB_MIN_H = 360;
const VB_PADDING = 40;

export default {
  name: "RelationsNetwork",
  components: {
    IntralaboralNode,
    DomainNode,
    AnalysisDisclosure,
  },
  props: {
    effect: {
      type: String,
      required: true,
    },
    results: {
      type: Object,
      default() {
        return {};
      },
    },
  },
  data() {
    return {
      ANGLES: ANGLES,
      DOMAIN_COLORS: DOMAIN_COLORS,
      DOMAIN_WASH: DOMAIN_WASH,
      // design.md §3.4/§6.1 — separación mínima entre el borde de una burbuja
      // y la siguiente, igual que el "orbit = hubR + r + 36" del Artifact.
      domainGap: 36,
      // Ancla fija en un lienzo interno holgado — el viewBox visible se
      // recalcula aparte (fitViewBox) igual que el Artifact, así el
      // contenido nunca se desplaza al recortar/expandir la vista.
      hubCx: 500,
      hubCy: 480,
      expandedIntralaboral: false,
      vb: { x: 0, y: 0, w: 1000, h: 960 },
      vbRAF: null,
    };
  },
  computed: {
    ...mapState(["tokenUser"]),
    isAdmin() {
      return !!(this.tokenUser && this.tokenUser.admin);
    },
    mainAnalysis() {
      return this.results[this.effect];
    },
    extraAnalysis() {
      return this.results[this.effect + "_TOTALEXTRA"];
    },
    indivAnalysis() {
      return this.results[this.effect + "_INDICOIND"];
    },
    hasIntralaboral() {
      return !!this.mainAnalysis;
    },
    hasExtra() {
      return !!this.extraAnalysis;
    },
    hasIndiv() {
      return !!this.indivAnalysis;
    },
    isMainNoModel() {
      return this.hasIntralaboral && this.mainAnalysis.no_model === true;
    },
    effectLabel() {
      return this.effect === "Estres" ? "Estrés" : this.effect;
    },
    hubRadius() {
      return this.radiusFor(this.mainAnalysis);
    },
    hubTooltipConfig() {
      if (!this.hasIntralaboral) return { content: "" };
      if (this.mainAnalysis.no_model) {
        const warnings = this.mainAnalysis.warnings || [];
        const content =
          "<b>" +
          this.effectLabel +
          '</b><div class="rn-tt-sub">Sin modelo — muestra insuficiente</div>' +
          (this.mainAnalysis.n != null
            ? '<div class="rn-tt-row"><span>n efectivo</span><span>' +
              this.mainAnalysis.n +
              "</span></div>"
            : "") +
          (warnings.length ? '<div class="rn-tt-sub">' + warnings.join(" ") + "</div>" : "");
        return { content: content, html: true, classes: ["rn-tooltip"] };
      }
      const rows = nonConstantCoefficients(this.mainAnalysis)
        .slice()
        .sort(function(a, b) {
          return Math.abs(b.beta || 0) - Math.abs(a.beta || 0);
        })
        .map((c) => {
          const beta = c.beta >= 0 ? "+" + c.beta.toFixed(2) : "−" + Math.abs(c.beta).toFixed(2);
          return (
            '<div class="rn-tt-row"><span>' +
            getConditionLabel(c.variable) +
            "</span><span>" +
            beta +
            "</span></div>"
          );
        })
        .join("");
      const n = this.mainAnalysis.n;
      const nOriginal = this.mainAnalysis.n_original;
      const sub =
        "R² ajustado " +
        roundRSquaredAdj(this.mainAnalysis) +
        "%" +
        (n != null ? " · n=" + n + (nOriginal != null ? " de " + nOriginal : "") : "");
      return {
        content: "<b>" + this.effectLabel + "</b>" + '<div class="rn-tt-sub">' + sub + "</div>" + rows,
        html: true,
        classes: ["rn-tooltip"],
      };
    },
    // El nodo de dominio Intralaboral colapsado usa el mismo análisis
    // principal que el hub (R4.2/design.md §3.4/§3.6) — mismo radio que el
    // hub, no uno propio distinto.
    intraRadius() {
      return this.hasIntralaboral ? this.radiusFor(this.mainAnalysis) : 0;
    },
    extraRadius() {
      return this.hasExtra ? this.radiusFor(this.extraAnalysis) : 0;
    },
    indivRadius() {
      return this.hasIndiv ? this.radiusFor(this.indivAnalysis) : 0;
    },
    viewBox() {
      return this.vb.x + " " + this.vb.y + " " + this.vb.w + " " + this.vb.h;
    },
    intralaboralPos() {
      return this.polarPoint(
        this.ANGLES.intralaboral,
        this.hubRadius + this.intraRadius + this.domainGap
      );
    },
    extraPos() {
      return this.polarPoint(
        this.ANGLES.extralaboral,
        this.hubRadius + this.extraRadius + this.domainGap
      );
    },
    indivPos() {
      return this.polarPoint(
        this.ANGLES.individual,
        this.hubRadius + this.indivRadius + this.domainGap
      );
    },
  },
  watch: {
    effect() {
      this.expandedIntralaboral = false;
      this.$nextTick(this.fitViewBox);
    },
    expandedIntralaboral() {
      this.$nextTick(this.fitViewBox);
    },
  },
  mounted() {
    this.$nextTick(this.fitViewBox);
  },
  beforeDestroy() {
    if (this.vbRAF) cancelAnimationFrame(this.vbRAF);
  },
  methods: {
    // Mismo criterio que DomainNode.vue/IntralaboralNode.vue: sin
    // r_squared_adj que escalar en no_model, así que usan el mismo radio
    // fijo (NOMODEL_RADIUS) en vez de nodeSize — debe coincidir exactamente
    // con lo que esos componentes van a dibujar, si no la órbita (distancia
    // al hub) queda calculada para un círculo más chico del que realmente
    // se ve, y las burbujas se encajan.
    radiusFor(analysis) {
      if (!analysis) return 0;
      if (analysis.no_model) return NOMODEL_RADIUS;
      return nodeSize(roundRSquaredAdj(analysis));
    },
    polarPoint(angleDeg, radius) {
      const rad = (angleDeg * Math.PI) / 180;
      return {
        cx: this.hubCx + radius * Math.sin(rad),
        cy: this.hubCy - radius * Math.cos(rad),
      };
    },
    // De borde a borde (no de centro a centro): con el relleno "Sin modelo"
    // ahora translúcido (fill-opacity), una línea centro-a-centro se
    // notaría atravesando el círculo hasta la mitad en vez de detenerse en
    // el borde — mismo cálculo que describeEdge() del Artifact.
    edgePath(cx, cy, r0, px, py, r1) {
      const dx = px - cx;
      const dy = py - cy;
      const dist = Math.sqrt(dx * dx + dy * dy) || 1;
      const ux = dx / dist;
      const uy = dy / dist;
      const x1 = cx + ux * r0;
      const y1 = cy + uy * r0;
      const x2 = px - ux * r1;
      const y2 = py - uy * r1;
      return "M" + x1 + "," + y1 + " L" + x2 + "," + y2;
    },
    onHubClick() {
      if (this.expandedIntralaboral) {
        this.expandedIntralaboral = false;
      }
    },
    onToggleIntralaboral() {
      this.expandedIntralaboral = !this.expandedIntralaboral;
    },
    // design.md corrección 2026-09-15 — "transición zoom": el viewBox se
    // recalcula según lo que de verdad está dibujado (getBBox), igual que
    // fitViewBox()/animateViewBoxTo() del Artifact. Sin esto, el lienzo
    // reservaba siempre el espacio del peor caso (todo expandido), dejando
    // mucho margen vacío arriba/abajo cuando no lo estaba.
    fitViewBox() {
      const content = this.$refs.content;
      if (!content) return;
      let bbox;
      try {
        bbox = content.getBBox();
      } catch (e) {
        return;
      }
      if (!bbox || bbox.width < 10 || bbox.height < 10) return;
      let x = bbox.x - VB_PADDING;
      let y = bbox.y - VB_PADDING;
      let w = bbox.width + VB_PADDING * 2;
      let h = bbox.height + VB_PADDING * 2;
      if (w < VB_MIN_W) {
        x -= (VB_MIN_W - w) / 2;
        w = VB_MIN_W;
      }
      if (h < VB_MIN_H) {
        y -= (VB_MIN_H - h) / 2;
        h = VB_MIN_H;
      }
      this.animateViewBoxTo(x, y, w, h);
    },
    animateViewBoxTo(tx, ty, tw, th) {
      const from = this.vb;
      const reduced =
        window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (reduced || (from.x === tx && from.y === ty && from.w === tw && from.h === th)) {
        this.vb = { x: tx, y: ty, w: tw, h: th };
        return;
      }
      if (this.vbRAF) cancelAnimationFrame(this.vbRAF);
      const fx = from.x;
      const fy = from.y;
      const fw = from.w;
      const fh = from.h;
      const duration = 480;
      let start = null;
      const step = (ts) => {
        if (start === null) start = ts;
        const t = Math.min(1, (ts - start) / duration);
        const e = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
        this.vb = {
          x: fx + (tx - fx) * e,
          y: fy + (ty - fy) * e,
          w: fw + (tw - fw) * e,
          h: fh + (th - fh) * e,
        };
        if (t < 1) this.vbRAF = requestAnimationFrame(step);
      };
      this.vbRAF = requestAnimationFrame(step);
    },
  },
};
</script>

<style lang="scss" scoped>
.rn-wrapper {
  width: 100%;
  // El Artifact vive dentro de un `.wrap{max-width:1040px}` fijo — el
  // lienzo de la Red nunca pasa de ~900px de ancho ahí, sin importar la
  // pantalla. Este panel, en cambio, se estira al ancho real del tablero
  // (mucho más ancho que 1040px en un monitor grande), y el viewBox se
  // ajusta siempre al contenido (fitViewBox) — sin techo, entre más ancho
  // el contenedor, más grandes las burbujas. Un máximo fijo, centrado,
  // reproduce la proporción real del Artifact sin importar la pantalla.
  max-width: 720px;
  margin: 0 auto;
}

.rn-svg {
  width: 100%;
  min-height: 360px;
}

.rn-link {
  fill: none;
  stroke-width: 2;
  opacity: 0.85;
}

.rn-hub-circle {
  fill: #f5eefc;
  stroke: #510087;
  stroke-width: 3.5;
  cursor: pointer;
  outline: none;
}

.rn-hub-circle:focus-visible {
  outline: 2px solid #2a78d6;
  outline-offset: 2px;
}

@media (prefers-reduced-motion: no-preference) {
  .rn-hub-circle {
    transition: r 0.4s cubic-bezier(0.34, 1.3, 0.64, 1);
  }
}

.rn-hub-label {
  font-family: "Poppins", sans-serif;
  font-size: 13px;
  font-weight: 800;
  fill: #510087;
  pointer-events: none;
}

.rn-hub-circle--nomodel {
  fill: #f4cdcd;
  fill-opacity: 0.45;
  stroke: #f6adad;
  stroke-dasharray: 4 3;
}

.rn-hub-label--nomodel {
  fill: #9b3d3d;
}

.rn-disclosures {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid #e7e1e7;
}

.rn-disclosure-item {
  flex: 1 1 220px;
  min-width: 220px;

  &__label {
    font-family: "Poppins", sans-serif;
    font-weight: 700;
    font-size: 0.78rem;
    color: #5c5568;
    margin: 0 0 2px;
  }
}

.rn-legend {
  margin-top: 14px;
  font-size: 12.5px;

  summary {
    cursor: pointer;
    color: #5c5568;
    font-weight: 600;
    list-style: none;
    display: flex;
    align-items: center;
    gap: 6px;

    &::-webkit-details-marker {
      display: none;
    }

    &:before {
      content: "›";
      display: inline-block;
      transition: transform 0.15s;
      color: #948da3;
    }
  }

  &[open] summary:before {
    transform: rotate(90deg);
  }
}

.rn-legend-body {
  margin-top: 10px;
  padding: 12px 14px;
  background: #f4f0ee;
  border-radius: 8px;
  color: #5c5568;
}
</style>



// WEBPACK FOOTER //
// src/components/frpm/components/variables-prioritarias/RelationsNetwork.vue